// src/components/ScoreboardAdmin/ControlButtonsComponent.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaVolumeUp,
  FaUndo,
  FaTrophy,
  FaChevronDown,
  FaCheck,
} from "react-icons/fa";

const DEFAULT_CHOICES = [
  { value: "/son/gong.mp3", label: "Gong classique" },
  { value: "/son/beep.mp3", label: "Beep" },
  { value: "/son/sirene.mp3", label: "Sirène" },
  { value: "/son/zen-gong.mp3", label: "Zen Gong" },
  { value: "/son/gong-2.mp3", label: "Gong 2" },
  { value: "custom", label: "URL personnalisée…" },
];

const ControlButtonsComponent = ({
  setModalVisible,
  setChrono,
  setIsChronoRunning,
  setScore1,
  setScore2,
  setPenalites1,
  setPenalites2,
  setOsaekomi1,
  setOsaekomi2,
  playGongSound, // fallback si pb
  setGoldenChrono,
  setGoldenScoreMode,
  setForfaitActif1,
  setForfaitActif2,
  competitionId,
  tapisId,
  chronoMax,
}) => {
  /** ---------------- Gong selector (persisté) ---------------- */
  const storageKey = useMemo(() => {
    const comp = competitionId ?? "any";
    const tatami = tapisId ?? "any";
    return `gongSound:${comp}:${tatami}`;
  }, [competitionId, tapisId]);

  const [choices] = useState(DEFAULT_CHOICES);
  const [selected, setSelected] = useState(choices[0].value);
  const [customUrl, setCustomUrl] = useState("");
  const audioRef = useRef(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const popRef = useRef(null);

  // Charger depuis localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      if (parsed?.value === "custom" && parsed?.url) {
        setSelected("custom");
        setCustomUrl(parsed.url);
      } else if (parsed?.value) {
        setSelected(parsed.value);
      }
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  // Cliquer à l’extérieur ferme le menu
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menuOpen]);

  const persistSelection = (value, url = "") => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ value, url }));
    } catch {
      /* ignore */
    }
  };

  const playSrc = (src) => {
    if (!src || src === "custom") return;
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      const a = new Audio(src);
      audioRef.current = a;
      a.play().catch(() => {
        if (typeof playGongSound === "function") playGongSound();
      });
    } catch {
      if (typeof playGongSound === "function") playGongSound();
    }
  };

  const playCurrent = () => {
    const src = selected === "custom" ? customUrl.trim() : selected;
    if (src) playSrc(src);
    else if (typeof playGongSound === "function") playGongSound();
  };

  const chooseAndPlay = (value) => {
    setSelected(value);
    if (value !== "custom") {
      setCustomUrl("");
      persistSelection(value, "");
      playSrc(value);
      setMenuOpen(false);
    }
  };

  const confirmCustom = () => {
    const url = (customUrl || "").trim();
    if (!url) return;
    persistSelection("custom", url);
    playSrc(url);
    setMenuOpen(false);
  };

  /** ---------------- Reset all ---------------- */
  const resetAll = () => {
    setIsChronoRunning(false);
    setChrono(chronoMax);
    setGoldenChrono(0);
    setGoldenScoreMode(false);
    setScore1({ ippon: 0, wazari: 0, yuko: 0 });
    setScore2({ ippon: 0, wazari: 0, yuko: 0 });
    setPenalites1(0);
    setPenalites2(0);
    setOsaekomi1(0);
    setOsaekomi2(0);
    setForfaitActif1(false);
    setForfaitActif2(false);
  };

  /** ---------------- UI helpers ---------------- */
  const Btn = ({ children, className = "", onClick, title }) => (
    <button
      title={title}
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 sm:px-5 sm:py-2.5 text-sm sm:text-base font-semibold shadow-md transition active:scale-[0.98] ${className}`}
    >
      {children}
    </button>
  );

  return (
    <div className="flex flex-col items-center justify-center gap-3">
      {/* Ligne d’action principale */}
      <div className="flex flex-wrap justify-center gap-3 mt-6">
        {/* Split button Gong */}
        <div className="relative" ref={popRef}>
          <div className="inline-flex overflow-hidden rounded-xl border border-white/10 shadow-md">
            {/* Bouton principal : joue le son courant */}
            <button
              type="button"
              onClick={playCurrent}
              className="bg-amber-400 text-black hover:bg-amber-300 active:bg-amber-400 px-4 py-2 sm:px-5 sm:py-2.5 text-sm sm:text-base font-semibold inline-flex items-center gap-2"
              title="Jouer le gong"
            >
              <FaVolumeUp className="text-lg" />
              Gong
            </button>
            {/* Bouton caret : ouvre le menu */}
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="bg-amber-500/90 hover:bg-amber-500 text-black px-2 sm:px-3"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              title="Choisir un son"
            >
              <FaChevronDown />
            </button>
          </div>

          {/* Menu compact */}
          {menuOpen && (
            <div
              role="menu"
              className="absolute z-20 mt-2 w-60 sm:w-72 rounded-lg border border-white/10 bg-slate-900 text-white shadow-xl p-2"
            >
              <div className="max-h-64 overflow-y-auto pr-1">
                {choices.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => chooseAndPlay(c.value)}
                    disabled={c.value === "custom"}
                    className={`w-full text-left px-3 py-2 rounded-md hover:bg-slate-800 flex items-center justify-between ${
                      selected === c.value && c.value !== "custom"
                        ? "bg-slate-800"
                        : ""
                    }`}
                  >
                    <span>{c.label}</span>
                    {selected === c.value && c.value !== "custom" && (
                      <FaCheck className="opacity-80" />
                    )}
                  </button>
                ))}

                {/* L’option URL personnalisée */}
                <div className="mt-2 px-2">
                  <div className="text-xs uppercase tracking-wide text-white/60 mb-1">
                    URL personnalisée
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="https://…/mon-gong.mp3"
                      value={customUrl}
                      onChange={(e) => setCustomUrl(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && confirmCustom()}
                      className="flex-1 rounded-md bg-slate-800 text-white border border-white/10 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-amber-400/60"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setSelected("custom");
                        confirmCustom();
                      }}
                      className="rounded-md bg-amber-500 hover:bg-amber-400 text-black px-3 py-1.5 text-sm font-semibold"
                      title="Valider l’URL et jouer"
                    >
                      OK
                    </button>
                  </div>
                  {selected === "custom" && !customUrl.trim() && (
                    <div className="mt-1 text-xs text-amber-300/90">
                      Collez une URL .mp3 valide puis cliquez « OK ».
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Reset tout */}
        <Btn
          onClick={resetAll}
          className="bg-rose-500 text-white hover:bg-rose-400 active:bg-rose-500"
          title="Remettre tous les compteurs à zéro"
        >
          <FaUndo className="text-lg" />
          Réinitialiser
        </Btn>

        {/* Fin du combat */}
        <Btn
          onClick={() => setModalVisible(true)}
          className="bg-indigo-600 text-white hover:bg-indigo-500 active:bg-indigo-600"
          title="Clôturer et saisir le vainqueur"
        >
          <FaTrophy className="text-lg" />
          Fin du combat
        </Btn>
      </div>
    </div>
  );
};

export default ControlButtonsComponent;
